import React, { ChangeEvent, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Dialog,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import DialogTitle from "@mui/material/DialogTitle";
import CloseIcon from "@mui/icons-material/Close";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { Day } from "@jamezz/react-components";
import { OpeningHoursForDay2 } from "./OpeningHoursSettings.tsx";

type ApplyOnMultipleDaysDialogProps<T> = {
  day: Day;
  timeRanges: OpeningHoursForDay2<T>[];
  open: boolean;
  onSave: (
    selectedDays: Omit<SelectedDays, "allDays" | "weekdays" | "weekend">,
    timeRanges: OpeningHoursForDay2<T>[]
  ) => void;
  onClose: () => void;
};

export type SelectedDays = {
  allDays: boolean;
  weekdays: boolean;
  weekend: boolean;
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
  sunday: boolean;
};

export const ApplyOnMultipleDaysDialog = <T,>({
  day,
  timeRanges,
  open,
  onSave,
  onClose,
}: ApplyOnMultipleDaysDialogProps<T>) => {
  const [selectedDays, setSelectedDays] = useState<SelectedDays>({
    allDays: false,
    weekdays: false,
    weekend: false,
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
  });

  useMemo(() => {
    setSelectedDays((prevState) => {
      return {
        ...prevState,
        allDays: false,
        weekdays: false,
        weekend: false,
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false,
        [day]: true,
      };
    });
  }, [day]);

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;

    setSelectedDays((prevState) => {
      if (name === "allDays") {
        return {
          ...prevState,
          allDays: checked,
          weekdays: false,
          weekend: false,
          monday: checked,
          tuesday: checked,
          wednesday: checked,
          thursday: checked,
          friday: checked,
          saturday: checked,
          sunday: checked,
          [day]: true,
        };
      }

      if (name === "weekdays") {
        return {
          ...prevState,
          allDays: false,
          weekdays: checked,
          weekend: false,
          monday: checked,
          tuesday: checked,
          wednesday: checked,
          thursday: checked,
          friday: checked,
          saturday: false,
          sunday: false,
          [day]: true,
        };
      }

      if (name === "weekend") {
        return {
          ...prevState,
          allDays: false,
          weekdays: false,
          weekend: checked,
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: checked,
          sunday: checked,
          [day]: true,
        };
      }

      return {
        ...prevState,
        [name]: checked,
        [day]: true,
      };
    });
  };

  return (
    <Dialog open={open}>
      <DialogTitle sx={{ m: 0, p: 2 }} id={"customized-dialog-title"}>
        <Typography>
          Select the days to apply
          {timeRanges.map((value, index) => {
            if (timeRanges.length - 1 === index) {
              return ` ${value.hours} `;
            }

            return ` ${value.hours} and `;
          })}
          to.
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label={"close"}
        onClick={onClose}
        sx={(theme) => ({
          position: "absolute",
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <Box sx={{ padding: 3 }}>
          <FormControl component={"fieldset"}>
            <FormLabel component={"legend"}>Select Days</FormLabel>
            <FormGroup>
              <FormControlLabel
                control={<Checkbox checked={selectedDays.allDays} onChange={handleCheckboxChange} name={"allDays"} />}
                label={"All Days"}
              />

              <FormControlLabel
                control={<Checkbox checked={selectedDays.weekdays} onChange={handleCheckboxChange} name={"weekdays"} />}
                label={"Weekdays"}
              />

              <FormControlLabel
                control={<Checkbox checked={selectedDays.weekend} onChange={handleCheckboxChange} name={"weekend"} />}
                label={"Weekend"}
              />
              <Grid container>
                {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((checkboxDay) => (
                  <Grid key={checkboxDay}>
                    <FormControlLabel
                      key={checkboxDay}
                      control={
                        <Checkbox
                          checked={selectedDays[checkboxDay as Day]}
                          onChange={handleCheckboxChange}
                          name={checkboxDay}
                          disabled={checkboxDay === day}
                        />
                      }
                      label={checkboxDay.charAt(0).toUpperCase() + checkboxDay.slice(1)}
                    />
                  </Grid>
                ))}
              </Grid>
            </FormGroup>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          type={"submit"}
          autoFocus
          onClick={() => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { allDays, weekdays, weekend, ...days } = selectedDays;
            onSave(days, timeRanges);
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
